import React, { JSX, useEffect } from "react";
import { PatientVolumeLabelEnum } from "@app/projects/enumerators";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider";
import { PCAPayerParticipation } from "@app/projects/results/servicing-provider/servicingProviderDetail";
import { EllipsisTooltip, InfoIndicator } from "@app/shared";
import { valueOrDashes } from "@app/shared/utility";
import { ColumnConfig, ColumnGroup, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { WafflesThemes } from "@app/shared/waffles/exports";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { Features } from "@app/feature-flags";

interface ClaimsProviderInsightsProps {
  record: ServicingProviderDetail;
  showVA: boolean;
  showTricare: boolean
}

interface ClaimsProviderInsightsWaffleToppings {
  [wafflesKeyColumnName]: string;
  payer: JSX.Element;
  percent: string;
}

const ClaimsProviderInsights = (props: ClaimsProviderInsightsProps): JSX.Element => {
  const [payerWidth, setPayerWidth] = React.useState<number>(0);
  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      setPayerWidth(event[0].contentBoxSize[0].inlineSize);
    });

    resizeObserver.observe(document.getElementById("payer-participation-reference"));
  }, []);

  const getColumnGroups = (lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum): ColumnGroup<ClaimsProviderInsightsWaffleToppings>[] => {
    return (
      [
        {
          children: ["payer", "percent"],
          key: '', /*no longer to show the title*/
          testId: lineOfBusiness.toLowerCase(),
        },
      ]
    );
  }

  const getColumnConfig = (lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum): ColumnConfig => {
    return ({
      payer: {
        displayText: "Payer",
        noResize: true,
        noSort: true,
        sizeContentToFit: true,
        testId: `${lineOfBusiness}-payer`,
      },
      percent: {        
        displayText: "Percent Total",
        minWidth: "95px",
        noResize: true,
        noSort: true,       
        sizeContentToFit: true,
        testId: `${lineOfBusiness}-percent`,
      },
    });
  };

  const getData = (lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum): ClaimsProviderInsightsWaffleToppings[] => {
    if (!props.record) {
      return [];
    }

    const buildToppings = (data: PCAPayerParticipation[]): ClaimsProviderInsightsWaffleToppings[] => {
      if (!data || data.length === 0) {
        // adding a fake row with "---" as the values in order to depict no data
        // instead of the giant default "no records..." waffles text
        data = [{
          claimsVolume: null,
          payerName: "---",
        }
        ];
      }

      return data.map((x, index) => {
        /* eslint-disable sort-keys */
        return {
          [wafflesKeyColumnName]: index.toString(),          
          percent: x.claimsVolume ? `${x.claimsVolume.toFixed(1)}%` : "---",
          payer: (
            <EllipsisTooltip
              width={payerWidth ? `${payerWidth / 2}px` : "auto"}
              text={x.payerName}
            />
          ),
        };
        /* eslint-enable sort-keys */
      });
    }

    switch (lineOfBusiness) {
      case ClaimsActivityLineOfBusinessTypeEnum.Commercial:
        return buildToppings(props.record.commercialParticipationDisplay);
      case ClaimsActivityLineOfBusinessTypeEnum.Medicaid:
        return buildToppings(props.record.medicaidParticipationDisplay);
      case ClaimsActivityLineOfBusinessTypeEnum.Medicare:
        return buildToppings(props.record.medicareParticipationDisplay);
      case ClaimsActivityLineOfBusinessTypeEnum.VA:
        return buildToppings(props.record.vaParticipationDisplay);
      case ClaimsActivityLineOfBusinessTypeEnum.TRICARE:
        return buildToppings(props.record.tricareParticipationDisplay);
      case ClaimsActivityLineOfBusinessTypeEnum.Total:
        return buildToppings(props.record.totalParticipationDisplay);
      default:
        return [];
    }
  }

  const renderInfoIndicator = (definition: string): JSX.Element => {
    return (
      <>
        <span>&nbsp;</span>
        <InfoIndicator className={"informational-indicator"} tooltipTitle={definition} />
      </>
    );
  }

  const getClaimsVolumeDefinition = (label: string): string => {
    switch (label) {
      case PatientVolumeLabelEnum.Ghost:
        return "0 patients";
      case PatientVolumeLabelEnum.Peripheral:
        return "<24th percentile in patient volume (based on population density and specialty)";
      case PatientVolumeLabelEnum.Standard:
        return "25th-74th percentile in patient volume (based on population density and specialty)";
      case PatientVolumeLabelEnum.Core:
        return "75th+ percentile in patient volume (based on population density and specialty)";
      case PatientVolumeLabelEnum.InsufficientDataNew:
        return "There is no claims activity for this provider but because the NPI was recently issued they have not been labeled as a ghost";
      case PatientVolumeLabelEnum.InsufficientDataSpecialty:
        return "There is no claims activity for this provider but because of limited claims volume for this specialty they have not been labeled as a ghost";
      case PatientVolumeLabelEnum.InsufficientDataStateLOB:
        return "There is no claims activity for this provider but because of limited claims coverage in this State/LOB they have not been labeled as a ghost";
      case PatientVolumeLabelEnum.RXOnly:
        return "Claims in which only Prescriptions are present";
      default:
        return;
    }
  }

  return (
    <React.Fragment>
      <div className="row">
        <div className="data-box col-12 col-sm-6">
          <div className="data-box-header">
            Effectiveness (Quality)
            {renderInfoIndicator("Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care")}
          </div>
          <div className="data-box-content">
            {props.record?.effectivenessRank ? `${props.record.effectivenessRank}/5` : "---"}
          </div>
        </div>

        <div className="data-box col-12 col-sm-6">
          <div className="data-box-header">
            Efficiency (Cost)
            {renderInfoIndicator("Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost).  Efficiency measures are based on 1) cost per visit 2) cost per patient.  Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences")}
          </div>
          <div className="data-box-content">
            {props.record?.efficiencyRank ? `${props.record.efficiencyRank}/5` : "---"}
          </div>
        </div>
      </div>


      <div className="row">
        <div className="data-box col-12 col-sm-6 align-top">
          <div className="data-box-header">Top Conditions</div>
          <div className="data-box-content">
            {props.record?.topConditionsDisplay?.length ? props.record.topConditionsDisplay.map((tc, index) => (
              <EllipsisTooltip
                key={index}
                text={tc}
              />

            )) : "---"}
          </div>
        </div>

        <div className="data-box col-12 col-sm-6 align-top">
          <div className="data-box-header">Top Procedures</div>
          <div className="data-box-content">
            {props.record?.topProceduresDisplay?.length ? props.record.topProceduresDisplay.map((tp, index) => (
              <EllipsisTooltip
                key={index}
                text={tp}
              />
            )) : "---"}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="data-box col-12 col-sm-6 col-lg-3 align-top">
          <div className="data-box-header">
            Total             
          </div>
          <div className="data-box-content">
            Claims Volume: {valueOrDashes(props.record?.totalClaimsVolumeDisplay, false)} {renderInfoIndicator(getClaimsVolumeDefinition(props.record?.totalClaimsVolumeDisplay))} 
          </div>
          <div className="data-box-content">
            Total ($): {valueOrDashes(props.record?.totalClaimAmount, true)}
          </div>
          {featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && (
            <div className="data-box-content">
              New Patient Claims: {valueOrDashes(props.record?.totalNewPatientInd, false)}
            </div>)}
          <div className="data-box-content" key={`${ClaimsActivityLineOfBusinessTypeEnum.Total}-${0}`}>
            <Waffles<ClaimsProviderInsightsWaffleToppings>
              columns={getColumnConfig(ClaimsActivityLineOfBusinessTypeEnum.Total)}
              columnGroups={getColumnGroups(ClaimsActivityLineOfBusinessTypeEnum.Total)}
              data={getData(ClaimsActivityLineOfBusinessTypeEnum.Total) || []}
              fullWidth={true}
              testId={`servicing-provider-profile__claims-provider-insights-${ClaimsActivityLineOfBusinessTypeEnum.Total}-${0}`}
              theme={WafflesThemes.Flyout}
            />
          </div>
        </div>

        <div className="data-box col-12 col-sm-6 col-lg-3 align-top">
          <div className="data-box-header">
            Medicare
          </div>
          <div className="data-box-content">
            Claims Volume: {valueOrDashes(props.record?.medicareClaimsVolumeDisplay, false)} {renderInfoIndicator(getClaimsVolumeDefinition(props.record?.medicareClaimsVolumeDisplay))}
          </div>
          <div className="data-box-content">
            Total ($): {valueOrDashes(props.record?.medicareClaimAmount, true)}
          </div>
          {featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && (
            <div className="data-box-content">
              New Patient Claims: {valueOrDashes(props.record?.medicareNewPatientInd, false)}
            </div>)}
          <div className="data-box-content" key={`${ClaimsActivityLineOfBusinessTypeEnum.Medicare}-${1}`}>
            <Waffles<ClaimsProviderInsightsWaffleToppings>
              columns={getColumnConfig(ClaimsActivityLineOfBusinessTypeEnum.Medicare)}
              columnGroups={getColumnGroups(ClaimsActivityLineOfBusinessTypeEnum.Medicare)}
              data={getData(ClaimsActivityLineOfBusinessTypeEnum.Medicare) || []}
              fullWidth={true}
              testId={`servicing-provider-profile__claims-provider-insights-${ClaimsActivityLineOfBusinessTypeEnum.Medicare}-${1}`}
              theme={WafflesThemes.Flyout}
            />
          </div>
        </div>

        <div className="data-box col-12 col-sm-6 col-lg-3 align-top">
          <div className="data-box-header">
            Medicaid
          </div>
          <div className="data-box-content">
            Claims Volume: {valueOrDashes(props.record?.medicaidClaimsVolumeDisplay, false)} {renderInfoIndicator(getClaimsVolumeDefinition(props.record?.medicaidClaimsVolumeDisplay))}
          </div>
          <div className="data-box-content">
            Total ($): {valueOrDashes(props.record?.medicaidClaimAmount, true)}
          </div>
          {featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && (
          <div className="data-box-content">
            New Patient Claims: {valueOrDashes(props.record?.medicaidNewPatientInd, false)}
          </div>)}
          <div className="data-box-content" key={`${ClaimsActivityLineOfBusinessTypeEnum.Medicaid}-${2}`}>
            <Waffles<ClaimsProviderInsightsWaffleToppings>
              columns={getColumnConfig(ClaimsActivityLineOfBusinessTypeEnum.Medicaid)}
              columnGroups={getColumnGroups(ClaimsActivityLineOfBusinessTypeEnum.Medicaid)}
              data={getData(ClaimsActivityLineOfBusinessTypeEnum.Medicaid) || []}
              fullWidth={true}
              testId={`servicing-provider-profile__claims-provider-insights-${ClaimsActivityLineOfBusinessTypeEnum.Medicaid}-${2}`}
              theme={WafflesThemes.Flyout}
            />
          </div>
        </div>

        <div className="data-box col-12 col-sm-6 col-lg-3 align-top" id="payer-participation-reference">
          <div className="data-box-header">
            Commercial
          </div>
          <div className="data-box-content">
            Claims Volume: {valueOrDashes(props.record?.commercialClaimsVolumeDisplay, false)} {renderInfoIndicator(getClaimsVolumeDefinition(props.record?.commercialClaimsVolumeDisplay))}
          </div>
          <div className="data-box-content">
            Total ($): {valueOrDashes(props.record?.commercialClaimAmount, true)}
          </div>
          {featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && (
            <div className="data-box-content">
              New Patient Claims: {valueOrDashes(props.record?.commercialNewPatientInd, false)}
            </div>)}
          <div className="data-box-content" key={`${ClaimsActivityLineOfBusinessTypeEnum.Commercial}-${3}`}>
            <Waffles<ClaimsProviderInsightsWaffleToppings>
              columns={getColumnConfig(ClaimsActivityLineOfBusinessTypeEnum.Commercial)}
              columnGroups={getColumnGroups(ClaimsActivityLineOfBusinessTypeEnum.Commercial)}
              data={getData(ClaimsActivityLineOfBusinessTypeEnum.Commercial) || []}
              fullWidth={true}
              testId={`servicing-provider-profile__claims-provider-insights-${ClaimsActivityLineOfBusinessTypeEnum.Commercial}-${3}`}
              theme={WafflesThemes.Flyout}
            />
          </div>
        </div>
       
        {props.showVA ? (
          <div className="data-box col-12 col-sm-6 col-lg-3 align-top" id="payer-participation-reference">
            <div className="data-box-header">
              VA
            </div>
            <div className="data-box-content">
              Claims Volume: {valueOrDashes(props.record?.vaClaimsVolumeDisplay, false)} {renderInfoIndicator(getClaimsVolumeDefinition(props.record?.vaClaimsVolumeDisplay))}
            </div>
            <div className="data-box-content">
              Total ($): {valueOrDashes(props.record?.vaClaimAmount, true)}
            </div>
            {featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && (
              <div className="data-box-content">
                New Patient Claims: {valueOrDashes(props.record?.vaNewPatientInd, false)}
              </div>)}
            <div className="data-box-content" key={`${ClaimsActivityLineOfBusinessTypeEnum.VA}-${4}`}>
              <Waffles<ClaimsProviderInsightsWaffleToppings>
                columns={getColumnConfig(ClaimsActivityLineOfBusinessTypeEnum.VA)}
                columnGroups={getColumnGroups(ClaimsActivityLineOfBusinessTypeEnum.VA)}
                data={getData(ClaimsActivityLineOfBusinessTypeEnum.VA) || []}
                fullWidth={true}
                testId={`servicing-provider-profile__claims-provider-insights-${ClaimsActivityLineOfBusinessTypeEnum.VA}-${4}`}
                theme={WafflesThemes.Flyout}
              />
            </div>
          </div>
        ) : undefined}
          
        {props.showTricare ? (
          <div className="data-box col-12 col-sm-6 col-lg-3 align-top" id="payer-participation-reference">
            <div className="data-box-header">
              TRICARE
            </div>
            <div className="data-box-content">
              Claims Volume: {valueOrDashes(props.record?.tricareClaimsVolumeDisplay, false)} {renderInfoIndicator(getClaimsVolumeDefinition(props.record?.tricareClaimsVolumeDisplay))}
            </div>
            <div className="data-box-content">
              Total ($): {valueOrDashes(props.record?.tricareClaimAmount, true)}
            </div>
            {featureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && (
              <div className="data-box-content">
                New Patient Claims: {valueOrDashes(props.record?.tricareNewPatientInd, false)}
              </div>)}
            <div className="data-box-content" key={`${ClaimsActivityLineOfBusinessTypeEnum.TRICARE}-${5}`}>
              <Waffles<ClaimsProviderInsightsWaffleToppings>
                columns={getColumnConfig(ClaimsActivityLineOfBusinessTypeEnum.TRICARE)}
                columnGroups={getColumnGroups(ClaimsActivityLineOfBusinessTypeEnum.TRICARE)}
                data={getData(ClaimsActivityLineOfBusinessTypeEnum.TRICARE) || []}
                fullWidth={true}
                testId={`servicing-provider-profile__claims-provider-insights-${ClaimsActivityLineOfBusinessTypeEnum.TRICARE}-${5}`}
                theme={WafflesThemes.Flyout}
              />
            </div>
          </div>
        ) : undefined}
        
      </div>

    </React.Fragment>
  )
}

export default ClaimsProviderInsights;
